import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingCart, Heart, User, Home, Grid3x3, ClipboardList, LogOut, MessageCircle, X, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import Logo from "@/components/Logo";
import api from "@/lib/api";

function Header({ onSearch, searchValue, setSearchValue }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const [openMenu, setOpenMenu] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/katalog?search=${encodeURIComponent(searchValue.trim())}`);
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-[#E5E5E5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 sm:gap-6">
        <Logo variant="compact" size="md" className="shrink-0" />

        <form onSubmit={onSubmit} className="flex-1 max-w-2xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={18} />
            <Input
              data-testid="header-search-input"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Cari produk..."
              className="pl-10 h-11 rounded-xl bg-[#FAF9F6] border-[#E5E5E5] focus-visible:brand-ring-primary"
            />
          </div>
        </form>

        <div className="flex items-center gap-1 sm:gap-2">
          <Link to="/wishlist" className="hidden sm:block">
            <Button variant="ghost" size="icon" className="rounded-xl" data-testid="header-wishlist-btn">
              <Heart size={20} />
            </Button>
          </Link>
          <Link to="/cart">
            <Button variant="ghost" size="icon" className="rounded-xl relative" data-testid="header-cart-btn">
              <ShoppingCart size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 brand-bg-primary text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] grid place-items-center px-1">
                  {cartCount}
                </span>
              )}
            </Button>
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-xl gap-2 px-2 sm:px-3" data-testid="header-user-menu">
                  <div className="w-7 h-7 rounded-full brand-soft-bg brand-text-primary grid place-items-center font-bold text-xs">
                    {(user.name || user.email)[0].toUpperCase()}
                  </div>
                  <span className="hidden md:block text-sm font-semibold">{user.name?.split(" ")[0] || "Akun"}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User className="mr-2" size={16} /> Profil Saya
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/pesanan")} data-testid="menu-orders">
                  <ClipboardList className="mr-2" size={16} /> Pesanan Saya
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/wishlist")} data-testid="menu-wishlist">
                  <Heart className="mr-2" size={16} /> Wishlist
                </DropdownMenuItem>
                {(user.role === "admin" || user.role === "owner") && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      <Grid3x3 className="mr-2" size={16} /> Admin Panel
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="mr-2" size={16} /> Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" className="hidden sm:block">
              <Button className="brand-btn-primary rounded-xl font-bold" data-testid="header-login-btn">
                Masuk
              </Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

function BottomNav() {
  const location = useLocation();
  const { cartCount } = useCart();
  const items = [
    { to: "/", icon: Home, label: "Beranda" },
    { to: "/katalog", icon: Grid3x3, label: "Katalog" },
    { to: "/cart", icon: ShoppingCart, label: "Keranjang", badge: cartCount },
    { to: "/pesanan", icon: ClipboardList, label: "Pesanan" },
    { to: "/profile", icon: User, label: "Akun" },
  ];
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t border-[#E5E5E5] md:hidden">
      <div className="flex justify-around items-stretch py-1.5">
        {items.map((it) => {
          const active = location.pathname === it.to || (it.to !== "/" && location.pathname.startsWith(it.to));
          const Icon = it.icon;
          return (
            <Link
              key={it.to}
              to={it.to}
              data-testid={`bottomnav-${it.label.toLowerCase()}`}
              className={`flex-1 flex flex-col items-center gap-0.5 py-2 transition-colors ${
                active ? "brand-text-primary" : "text-[#5C5C5C]"
              }`}
            >
              <div className="relative">
                <Icon size={20} strokeWidth={active ? 2.5 : 2} />
                {it.badge > 0 && (
                  <span className="absolute -top-1.5 -right-2.5 brand-bg-primary text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] grid place-items-center px-1">
                    {it.badge}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-semibold">{it.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

function WhatsAppButton() {
  const { branding } = useBranding();
  const phone = branding.contact.whatsapp;
  if (!phone) return null;
  const brandName = branding.general.name;
  return (
    <a
      href={`https://wa.me/${phone}?text=${encodeURIComponent(`Halo ${brandName}, saya ingin tanya tentang produk.`)}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float-btn"
      className="fixed right-4 bottom-20 md:bottom-6 z-30 w-12 h-12 rounded-full bg-[#25D366] text-white grid place-items-center shadow-lg hover:scale-110 transition-transform"
    >
      <MessageCircle size={22} fill="white" />
    </a>
  );
}

function Footer() {
  const { branding } = useBranding();
  const [footerCats, setFooterCats] = useState([]);
  useEffect(() => {
    // Kategori dinamis dari tenant (bukan hardcode kategori telur).
    api.get("/categories").then((r) => setFooterCats((r.data || []).slice(0, 3))).catch(() => {});
  }, []);
  const wa = branding.contact.whatsapp;
  const address = branding.contact.address;
  const copyright = branding.footer.copyright || branding.general.name;
  const poweredBy = branding.footer.powered_by;
  return (
    <footer className="mt-16 border-t border-[#E5E5E5] bg-white">
      <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <Logo variant="compact" size="lg" to={null} />
          <p className="text-sm text-[#5C5C5C] max-w-md leading-relaxed mt-3">
            {branding.footer.text || branding.general.description || branding.general.tagline}
          </p>
          {address && <div className="mt-4 text-sm text-[#5C5C5C]">{address}</div>}
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--brand-primary)] font-bold mb-3">Belanja</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/katalog" className="hover:text-[color:var(--brand-primary)]">Semua Produk</Link></li>
            {footerCats.map((c) => (
              <li key={c.slug}>
                <Link to={`/katalog?category=${c.slug}`} className="hover:text-[color:var(--brand-primary)]">{c.name}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--brand-primary)] font-bold mb-3">Bantuan</div>
          <ul className="space-y-2 text-sm">
            {wa && (
              <li><a href={`https://wa.me/${wa}`} className="hover:text-[color:var(--brand-primary)]">WhatsApp: +{wa}</a></li>
            )}
            {branding.contact.email && (
              <li><a href={`mailto:${branding.contact.email}`} className="hover:text-[color:var(--brand-primary)]">Email: {branding.contact.email}</a></li>
            )}
            <li><Link to="/login" className="hover:text-[color:var(--brand-primary)]">Masuk / Daftar</Link></li>
            <li><Link to="/pesanan" className="hover:text-[color:var(--brand-primary)]">Lacak Pesanan</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#E5E5E5] py-4 text-center text-xs text-[#5C5C5C]" data-testid="footer-copyright">
        © {new Date().getFullYear()} {copyright}. All rights reserved.
        {poweredBy && <span className="ml-2 opacity-75">· Powered by {poweredBy}</span>}
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const [searchValue, setSearchValue] = useState("");
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--brand-background)" }}>
      <Header searchValue={searchValue} setSearchValue={setSearchValue} />
      <main className="flex-1 pb-20 md:pb-0">{children}</main>
      <Footer />
      <BottomNav />
      <WhatsAppButton />
    </div>
  );
}
